import React from "react"

import { useOrganizationUsersTransport } from "@/organization/transport"
import { Tooltip } from "@coval/ui/tooltip"
import { Check, Copy } from "lucide-react"

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard"

interface TestSetMetadataProps {
  testSetId?: string
  createdAt?: string
  lastUpdatedAt?: string
  createdByUserId?: string | null
}

export const TestSetMetadata: React.FC<TestSetMetadataProps> = ({
  testSetId,
  createdAt,
  lastUpdatedAt,
  createdByUserId,
}) => {
  const { copied, copy } = useCopyToClipboard()
  const { usersByUlid, isLoading, error } = useOrganizationUsersTransport()

  if (isLoading) {
    return (
      <div className="flex items-center gap-4 text-sm text-coval-text-muted">
        <span>Loading...</span>
      </div>
    )
  }
  if (error) {
    return (
      <div className="flex items-center gap-4 text-sm text-destructive">
        <span>Failed to load user data</span>
      </div>
    )
  }

  const createdBy = createdByUserId
    ? (usersByUlid[createdByUserId]?.displayName ?? "Unknown")
    : null

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return "N/A"
    }

    try {
      const date = new Date(dateString)
      const month = date.toLocaleDateString("en-US", { month: "short" })
      const day = date.getDate()
      const hour = date.getHours()
      const minute = date.getMinutes()
      const ampm = hour >= 12 ? "PM" : "AM"
      const displayHour = hour % 12 || 12
      const displayMinute = minute.toString().padStart(2, "0")
      return `${month} ${day} at ${displayHour}:${displayMinute} ${ampm}`
    } catch {
      return "Invalid date"
    }
  }

  const formatEditedDate = (dateString?: string) => {
    if (!dateString) {
      return "N/A"
    }

    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now.getTime() - date.getTime()
      const diffMins = Math.floor(diffMs / 60000)

      // If less than 1 minute, show "Just now"
      if (diffMins < 1) {
        return "Just now"
      }

      // If less than 1 hour, show minutes
      if (diffMins < 60) {
        return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`
      }

      // If less than 24 hours, show hours
      const diffHours = Math.floor(diffMins / 60)
      if (diffHours < 24) {
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`
      }

      // Otherwise show formatted date
      return formatDate(dateString)
    } catch {
      return "Invalid date"
    }
  }

  const handleCopyId = () => {
    if (!testSetId) {
      return
    }
    void copy(testSetId)
  }

  return (
    <div className="flex items-center gap-4 text-sm text-coval-text-muted shrink-0 whitespace-nowrap">
      {createdBy && (
        <div>
          <span>Created by {createdBy}</span>
        </div>
      )}
      {createdAt && (
        <div>
          <span>Created: {formatDate(createdAt)}</span>
        </div>
      )}

      {lastUpdatedAt && (
        <div>
          <span>Edited: {formatEditedDate(lastUpdatedAt)}</span>
        </div>
      )}

      {testSetId && (
        <div className="flex items-center gap-1">
          <span>ID: {testSetId.slice(0, 7)}</span>
          <Tooltip content="Copy ID">
            <button
              onClick={() => {
                handleCopyId()
              }}
              className="p-0.5 hover:bg-surface-1 rounded transition-colors"
              aria-label="Copy ID"
            >
              {copied ? (
                <Check className="w-3 h-3 text-coval-success-text" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  )
}
